import React, { useState, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/components/ui/icon';
import { toast } from "@/hooks/errors/use-toast";
import { useSignin } from '@/hooks/auth/use-signin';
import FadeIn from '@/components/animations/FadeIn';
import { Separator } from '@/components/ui/separator';

interface SignInFormProps {
  defaultEmail?: string;
  isShowingAccountExistsMessage?: boolean;
  onSwitchMode?: () => void; // Callback for switching to sign up mode
  onFormSubmit?: (email: string, password: string) => void; // Callback for form submission
}

// Main component that uses the modal captcha approach
const SignInForm: React.FC<SignInFormProps> = ({ 
  defaultEmail = '',
  isShowingAccountExistsMessage = false,
  onSwitchMode,
  onFormSubmit
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, loading } = useSignin();
  
  // Generate unique IDs for this form instance
  const formId = useId();
  const emailId = useId();
  const passwordId = useId();

  useEffect(() => {
    // Show message when redirected from signup with existing account
    if (isShowingAccountExistsMessage) {
      toast({
        title: "Account Already Exists",
        description: "This email is already registered. Please sign in.",
        variant: "warning",
      });
    }
  }, [isShowingAccountExistsMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Use callback if provided, otherwise show captcha modal
    if (onFormSubmit) {
      onFormSubmit(email, password);
    } else {
      // Fallback for standalone usage - this shouldn't happen in normal flow
      console.warn('SignInForm: No onFormSubmit callback provided');
    }
  };

  return (
    <motion.form 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      onSubmit={handleSubmit} 
      className="space-y-5" 
      id={formId}
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor={emailId} className="text-sm font-medium text-gray-700 dark:text-gray-200">Email</Label>
          <div className="relative">
            <Input 
              id={emailId} 
              type="email" 
              placeholder="you@example.com" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-11 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 focus-visible:border-accent/40 hover:border-accent/30 hover:shadow-md transition-all duration-300 shadow-sm"
              required
              autoComplete="email"
            />
            <Icon 
              name="Mail" 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent" 
              size={18} 
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor={passwordId} className="text-sm font-medium text-gray-700 dark:text-gray-200">Password</Label>
            <Button 
              variant="link" 
              className="p-0 h-auto text-xs text-accent hover:text-accent-foreground"
              type="button"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot password?
            </Button>
          </div>
          <div className="relative">
            <Input 
              id={passwordId} 
              type={showPassword ? "text" : "password"} 
              placeholder="••••••••" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 h-11 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 focus-visible:border-accent/40 hover:border-accent/30 hover:shadow-md transition-all duration-300 shadow-sm"
              required
              autoComplete="current-password"
            />
            <Icon 
              name="Lock" 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent" 
              size={18} 
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              onClick={() => setShowPassword(!showPassword)}
            >
              <Icon 
                name={showPassword ? "EyeOff" : "Eye"} 
                className="text-accent" 
                size={18} 
              />
            </Button>
          </div>
        </div>
      </div>
      
      <FadeIn delay={200}>
        <Button 
          type="submit" 
          className="w-full h-11 bg-gradient-to-r from-blue-400 via-blue-600 to-purple-500 text-white font-medium rounded-full shadow-md"
          disabled={loading}
        >
          {loading ? (
            <>
              <Icon name="Loader2" className="mr-2 animate-spin" size={18} />
              Signing In...
            </>
          ) : (
            <>
              Sign In
              <Icon name="ArrowRight" className="ml-2" size={18} />
            </>
          )}
        </Button>
      </FadeIn>

      {/* Compact divider with reduced vertical space */}
      <div className="relative flex py-2.5 items-center my-1.5">
        <div className="flex-grow border-t border-gray-300 dark:border-gray-700"></div>
        <span className="flex-shrink-0 mx-3 text-xs text-gray-500 dark:text-gray-400">or</span>
        <div className="flex-grow border-t border-gray-300 dark:border-gray-700"></div>
      </div>

      <Button 
        type="button" 
        variant="outline"
        className="w-full h-11 bg-white dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 text-blue-500 dark:text-blue-300 rounded-full font-medium shadow-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-400 dark:hover:text-blue-300 transform transition-all duration-200 ease-out hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] active:shadow-md" 
        onClick={onSwitchMode || (() => navigate('/signup'))}
      >
        Create a New Account
      </Button>
      
      <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
        We hope you enjoy our free tools!
      </p>
    </motion.form>
  );
};

export default SignInForm;
